import { ComponentProps } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';

import { cn } from '@/lib/utils';

import { ProductCardContext } from './context';

type ProductCardRootProps = ComponentProps<'div'>;

/**
 * A product card with two faces: the offer, and the what's-included panel that slides over it.
 *
 * Owns the whole mechanism — the flip state, the slide, and the height animation — so the four
 * cards that use it carry only their own content.
 *
 * The container is measured rather than given a fixed height because the two faces are different
 * heights and both of them change: the front grows an eligibility line, the back's bullet list
 * comes from Stripe and nobody here knows how long it is.
 */
const ProductCardRoot = ({ className, children, ...props }: ProductCardRootProps) => {
  const [showWhatsIncluded, setShowWhatsIncluded] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const frontRef = useRef<HTMLDivElement>(null);
  const whatsIncludedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    const children = showWhatsIncluded ? whatsIncludedRef.current : frontRef.current;
    if (!container || !children) return undefined;
    container.style.height = `${children.getBoundingClientRect().height}px`;
    const resizeObserver = new ResizeObserver((entries) => {
      const height = entries[0]?.borderBoxSize[0]?.blockSize || children.getBoundingClientRect().height;
      if (height == null) return;
      container.style.height = `${height}px`;
    });
    resizeObserver.observe(children);
    return () => {
      resizeObserver.unobserve(children);
      resizeObserver.disconnect();
    };
  }, [showWhatsIncluded]);

  return (
    <ProductCardContext.Provider value={{ showWhatsIncluded, setShowWhatsIncluded, frontRef, whatsIncludedRef }}>
      <div
        className={cn(
          'w-full max-w-[336px] overflow-hidden rounded-2xl bg-white shadow-[0px_4px_12px_0px_rgba(0,0,0,0.20)]',
          className,
        )}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      >
        <div ref={containerRef} className="relative w-full transition-[height]">
          {children}
        </div>
      </div>
    </ProductCardContext.Provider>
  );
};

export default ProductCardRoot;
